import { type Query, QueryClient } from '@tanstack/react-query'
import { notify } from '#/utils'
import { ServerError } from './ServerError'
import { flattenErrorBody } from './flattenErrorBody'

/**
 * On error Orval's fetch mutator throws either:
 * - TypeError
 * - NotAllowedError (DOMException)
 * - AbortError (DOMException)
 * - {@link ServerError}
 */
export type OrvalFetchError = TypeError | DOMException | ServerError

/**
 * Shown when neither backend nor browser gave us anything displayable (e.g. a
 * 500 with no body, or a dropped connection). Raw details still reach the
 * console, so this is the only string frontend owns - everything else is
 * backend copy.
 */
const getGenericErrorMessage = () => t('An error occurred')

/**
 * Extract a user-facing error message from Orval mutator errors.
 */
export function getApiErrorMessage(error: OrvalFetchError): string | null {
  if (error instanceof ServerError) {
    // A JSON body is backend copy written for the user, so it displays whatever the status - some endpoints answer a
    // 5xx with a message worth reading. Anything else gives null, and callers fall back to their own copy rather than
    // to an HTTP status.
    return flattenErrorBody(error.parsedResponse)
  }

  if (error instanceof TypeError || error instanceof DOMException) {
    return error.message
  }

  return null
}

/**
 * Returns messages from rejection filed under `field`, for displaying next to that input. Empty when the failure
 * belongs to no field, so callers can pass those on to {@link onErrorDefaultHandler}.
 *
 * Accepts both shapes seen in the wild: `{field: [message]}` and `{field: message}`.
 */
export function getFieldErrors(error: OrvalFetchError, field: string): string[] {
  if (!(error instanceof ServerError)) {
    return []
  }

  const messages = (error.parsedResponse as Record<string, unknown> | undefined)?.[field]
  if (typeof messages === 'string') {
    return [messages]
  }
  return Array.isArray(messages) ? messages.filter((message): message is string => typeof message === 'string') : []
}

/**
 * By default, every Orval request handles error with this routine, and the default can be overriden on per-hook basis.
 *
 * Error handling policy:
 * - backend owns error content: keep error messages updated in backend, don't ignore/override them on frontend.
 * - frontend owns error display: ideally nicely inline, by default with a toast using this default handler.
 *
 * Note that overriding default handler doesn't override snapshot restoration,
 * because that's part of the global handler instead.
 *
 * Example usages:
 * ```tsx
 *   query: {
 *     throwOnError: () => {                                      // BAD, don't override.
 *       notify(t('some custom message'), 'error')
 *       return false
 *     },
 *     throwOnError: () => null,                                  // BEST, but handle the error nicely inline instead!
 *     // no override                                             // GOOD, leave it to the default handler.
 *     throwOnError: (error: OrvalFetchError, query) => {         // GOOD, use callback for more than default handler.
 *       // Do more stuff.
 *       return onErrorDefaultHandler(error, query)
 *     },
 *   },
 *   mutation: {
 *     onError: () => notify(t('some custom message'), 'error'),  // BAD, don't override.
 *     onError: () => null,                                       // BEST, but handle the error nicely inline instead!
 *     // no override                                             // GOOD, leave it to the default handler.
 *     onError: (error: OrvalFetchError, variables, context) => { // GOOD, use callback for more than default handler.
 *       // Do more stuff.
 *       onErrorDefaultHandler(error, variables, context)
 *     },
 *   },
 * ```
 */
export function onErrorDefaultHandler(
  error: OrvalFetchError,
  _query: Query<unknown, OrvalFetchError, unknown, readonly unknown[]>,
): boolean
export function onErrorDefaultHandler(error: OrvalFetchError, _variables: unknown, _context: unknown): void
export function onErrorDefaultHandler(
  error: OrvalFetchError,
  _variablesOrQuery: unknown | Query<unknown, OrvalFetchError, unknown, readonly unknown[]>,
  _context?: unknown,
): boolean | void {
  if (error instanceof ServerError) {
    // Log the whole body rather than `detail`, which drops error page output on purpose. A suppressed traceback is
    // still the fastest way to find out what broke.
    let body: string | undefined
    try {
      body = typeof error.parsedResponse === 'string' ? error.parsedResponse : JSON.stringify(error.parsedResponse)
    } catch {
      body = String(error.parsedResponse)
    }
    notify(
      getApiErrorMessage(error) || getGenericErrorMessage(),
      'error',
      {},
      [`${error.name}: ${error.message}`, body].filter(Boolean).join(' | '),
    )
  } else if (error instanceof TypeError) {
    notify(getApiErrorMessage(error) || getGenericErrorMessage(), 'error', {}, `${error.name}: ${error.message}`)
  } else if (error instanceof DOMException && error.name === 'AbortError') {
    // Don't display error if user aborted the request.
  } else if (error instanceof DOMException && error.name === 'NotAllowedError') {
    notify(getApiErrorMessage(error) || getGenericErrorMessage(), 'error', {}, `${error.name}: ${error.message}`)
  } else {
    notify(getApiErrorMessage(error) || getGenericErrorMessage(), 'error', {}, `${error.name}: ${error.message}`)
  }

  // Query's `throwOnError` by default returns a false, whereas Mutation's `onError` return nothing.
  return _variablesOrQuery instanceof QueryClient ? false : undefined
}
